import { Box } from "@mui/system";
import Header from "components/Header";
import WidgetWrapper from "components/WidgetWrapper";
import React from "react";
import { useAssessments } from "./hooks/useAssessments";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolBar";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import AssessmentForm from "pages/assessment/components/assessmentForm";
import DeleteAssessmentConfirmationDialog from "./components/deleteAssessmentConfirmation";

function Assessments() {
  const {
    isLargeScreen,
    assessments,
    totalAssessments,
    showOrg,
    showDivision,
    showLevel,
    showSeniorLeader,
    isAcademyImprovementLead,
    selectedLocation,
    setSelectedLocation,
    selectedDivision,
    setSelectedDivision,
    selectedLevel,
    setSelectedLevel,
    selectedAssessor,
    setSelectedAssessor,
    selectedAssessee,
    setSelectedAssessee,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    locations,
    divisions,
    levels,
    assessors,
    assesseeNames,
    search,
    setSearch,
    searchInput,
    setSearchInput,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    columns,
    backgroundAlt,
    primaryLight,
    primaryDark,
    open,
    editMode,
    currentAssessment,
    handleClose,
    setOpen,
    handleDeleteAssessmentConfirm,
    deleteDialogOpen,
    closeDeleteDialog,
  } = useAssessments();

  return (
    <Box m={isLargeScreen ? "1rem" : "1.5rem 0.5rem 2rem 0.5rem"}>
      <WidgetWrapper>
        <Header
          title="ALT Coach - Assessments Table"
          subtitle="View all assessments"
        />

        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mt={3}>
          {(showOrg || isAcademyImprovementLead) && (
            <TextField
              select
              label="School"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc._id} value={loc._id}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {(showLevel || showSeniorLeader || isAcademyImprovementLead) && (
            <TextField
              select
              label="Department"
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Divisions</MenuItem>
              {divisions.map((div) => (
                <MenuItem key={div._id} value={div._id}>
                  {div.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {(showDivision || showSeniorLeader || isAcademyImprovementLead) && (
            <TextField
              select
              label="Year"
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Levels</MenuItem>
              {levels.map((lvl) => (
                <MenuItem key={lvl._id} value={lvl._id}>
                  {lvl.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            label="Assessor"
            value={selectedAssessor}
            onChange={(e) => setSelectedAssessor(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
          >
            <MenuItem value="">All Assessors</MenuItem>
            {assessors.map((assessor) => (
              <MenuItem key={assessor._id} value={assessor._id}>
                {assessor.firstName} {assessor.lastName}
              </MenuItem>
            ))}
          </TextField>

          {/* New Assessee Dropdown */}
          {(showSeniorLeader || isAcademyImprovementLead) && (
            <TextField
              select
              label="Assessee"
              value={selectedAssessee}
              onChange={(e) => setSelectedAssessee(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Assessments</MenuItem>
              {assesseeNames.map((name, index) => (
                <MenuItem key={index} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box
            display="flex"
            gap={2}
            alignItems="center"
            sx={{ minWidth: 200 }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 200 } },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 200 } },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        <Box
          mt="40px"
          sx={{
            "& .MuiDataGrid-root": {},
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflow: "visible",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: backgroundAlt,
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: backgroundAlt,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: primaryLight,
            },
            "& .MuiDataGrid-row": {
              height: "auto", // This ensures the row height adapts to content
            },
          }}
        >
          <DataGrid
            getRowId={(row) => row._id}
            rows={assessments}
            columns={columns}
            page={page - 1}
            pageSize={pageSize}
            rowCount={totalAssessments}
            rowsPerPageOptions={[10, 25, 50, 100]}
            pagination
            paginationMode="server"
            autoHeight
            rowHeight={50}
            sortingMode="server" // Enable server-side sorting
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize) => {
              setPage(1);
              setPageSize(newPageSize);
            }}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={(newFilterModel) => {
              setPage(1);
              setFilterModel(newFilterModel);
            }}
            // loading={assessments.length === 0}
            components={{
              Toolbar: DataGridCustomToolbar,
            }}
            componentsProps={{
              toolbar: {
                search,
                setSearch,
                searchInput,
                setSearchInput,
                sort,
                setSort,
                backgroundAlt,
                primaryLight,
                primaryDark,
              },
            }}
          />
        </Box>
      </WidgetWrapper>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? `Edit Assessment` : "Create a New Assessment"}
        </DialogTitle>
        <DialogContent>
          <AssessmentForm
            editMode={editMode}
            currentAssessment={currentAssessment}
            setOpen={setOpen}
            handleClose={handleClose}
          />
        </DialogContent>
      </Dialog>
      <DeleteAssessmentConfirmationDialog
        open={deleteDialogOpen}
        handleClose={closeDeleteDialog}
        handleDeleteAssessmentConfirm={handleDeleteAssessmentConfirm}
      />
    </Box>
  );
}

export default Assessments;
